"use client"

import { useState, useEffect, useMemo } from "react"
import { useRouter } from "next/navigation"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { HttpTypes } from "@medusajs/types"
import { useExpressCart } from "@/modules/express-checkout/providers/express-cart"
import Card from "../card"
import Button from "@/modules/common/components/button"
import Input from "@/modules/common/components/input"
import CountrySelect from "@/modules/checkout/components/country-select"

type ExpressAddressProps = {
  countryCode: string
  productHandle: string
  isActive: boolean
  isCompleted: boolean
}

/**
 * Address step in express checkout
 * Collects shipping address (billing = shipping for simplicity)
 */
const ExpressAddress = ({
  countryCode,
  productHandle,
  isActive,
  isCompleted,
}: ExpressAddressProps) => {
  const router = useRouter()
  const { cart, updateExpressCart, isLoading } = useExpressCart()

  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    address_1: "",
    postal_code: "",
    city: "",
    country_code: countryCode || "",
    province: "",
  })
  const [marketingOptIn, setMarketingOptIn] = useState(false)

  // Populate form with existing cart data
  useEffect(() => {
    if (cart?.shipping_address) {
      setFormData({
        email: cart.email || "",
        first_name: cart.shipping_address.first_name || "",
        last_name: cart.shipping_address.last_name || "",
        phone: cart.shipping_address.phone || "",
        address_1: cart.shipping_address.address_1 || "",
        postal_code: cart.shipping_address.postal_code || "",
        city: cart.shipping_address.city || "",
        country_code: cart.shipping_address.country_code || countryCode || "",
        province: cart.shipping_address.province || "",
      })
    }
    // Load marketing opt-in from cart metadata
    if (cart?.metadata?.marketing_opt_in !== undefined) {
      setMarketingOptIn(Boolean(cart.metadata.marketing_opt_in))
    }
  }, [cart?.shipping_address, cart?.email, cart?.metadata, countryCode])

  // Check if form is valid
  const isFormValid = useMemo(() => {
    return (
      formData.email &&
      formData.first_name &&
      formData.last_name &&
      formData.phone &&
      formData.address_1 &&
      formData.postal_code &&
      formData.city &&
      formData.country_code
    )
  }, [formData])

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Handle form submit
  const handleContinue = async () => {
    if (!isFormValid || !cart) return

    const addressData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      address_1: formData.address_1,
      address_2: "",
      postal_code: formData.postal_code,
      city: formData.city,
      country_code: formData.country_code,
      province: formData.province,
      company: "",
    }

    await updateExpressCart({
      email: formData.email,
      shipping_address: addressData,
      billing_address: addressData, // Same as shipping for express checkout
      metadata: {
        ...cart.metadata,
        marketing_opt_in: marketingOptIn,
      },
    })

    captureAnalyticsEvent("address_saved", {
      source: "express_checkout",
      country_code: formData.country_code,
      city: formData.city,
    })
    captureAnalyticsEvent("checkout_step_completed", {
      step: "shipping",
      checkout_type: "express",
    })

    router.push(
      `/${countryCode}/products/${productHandle}?express=true&step=shipping`
    )
  }

  // Handle edit
  const handleEdit = () => {
    router.push(
      `/${countryCode}/products/${productHandle}?express=true&step=address`
    )
  }

  return (
    <Card
      title="2. Naslov za dostavo"
      isActive={isActive}
      isCompleted={isCompleted}
      onEdit={handleEdit}
    >
      {isActive ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            handleContinue()
          }}
        >
          {/* Email */}
          <Input
            label="E-poštni naslov"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Ime"
              name="first_name"
              autoComplete="given-name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
            <Input
              label="Priimek"
              name="last_name"
              autoComplete="family-name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>

          {/* Phone */}
          <Input
            label="Telefon"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleChange}
            required
          />

          {/* Address */}
          <Input
            label="Naslov"
            name="address_1"
            autoComplete="address-line1"
            value={formData.address_1}
            onChange={handleChange}
            required
          />

          {/* Postal Code and City */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Poštna številka"
              name="postal_code"
              autoComplete="postal-code"
              value={formData.postal_code}
              onChange={handleChange}
              required
            />
            <Input
              label="Mesto"
              name="city"
              autoComplete="address-level2"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>

          {/* Country */}
          <CountrySelect
            name="country_code"
            autoComplete="country"
            region={cart?.region}
            value={formData.country_code}
            onChange={handleChange}
            required
          />

          {/* Hidden province field */}
          <input type="hidden" name="province" value={formData.province} />

          {/* Marketing Opt-In Checkbox */}
          <div className="mt-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="marketing_opt_in"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                data-testid="express-marketing-opt-in-checkbox"
              />
              <span className="text-sm text-neutral-700 group-hover:text-neutral-900">
                Želim prejemati novice, promocije in posebne ponudbe na svoj e-poštni naslov.
              </span>
            </label>
            <p className="text-xs text-neutral-500 mt-1 ml-7">
              Kadarkoli se lahko odjavite.
            </p>
          </div>

          {/* Continue Button */}
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!isFormValid}
            className="w-full mt-2"
            size="large"
          >
            Nadaljuj na izbiro dostave
          </Button>
        </form>
      ) : isCompleted && cart?.shipping_address ? (
        // Collapsed view when completed
        <div className="flex flex-col gap-1 pt-2 text-sm text-neutral-600">
          <span className="font-medium text-neutral-900">
            {cart.shipping_address.first_name} {cart.shipping_address.last_name}
          </span>
          <span>{cart.shipping_address.address_1}</span>
          <span>
            {cart.shipping_address.postal_code} {cart.shipping_address.city}
          </span>
          <span>{cart.email}</span>
        </div>
      ) : null}
    </Card>
  )
}

export default ExpressAddress




